import React, { useState, useEffect, memo, forwardRef } from 'react';
import { Input } from './ui/input';

/**
 * Auto-Markup typing input. Holds its value in LOCAL state so each keystroke
 * only re-renders this small input — not the entire BingoGenerator tree.
 *
 * This makes typing feel instant under heavy GPU load (e.g. Streamlabs / OBS
 * screen capture on Facebook Live), where the previous controlled-from-parent
 * pattern had every keypress trigger Sidebar + CanvasEditor reconciliation.
 *
 * On Enter (and only on Enter) the value is bubbled up via `onSubmit(value)`,
 * then cleared. External resets (e.g. "Clear All") flow through `resetKey` —
 * any change to that prop clears the input.
 *
 * The forwarded `ref` is attached to the underlying <input> so callers can
 * still call `.focus()` (used in BingoCallerPage's draggable Auto-Markup pane).
 */
const AutoMarkupInput = forwardRef(({
  onSubmit,
  resetKey,
  placeholder,
  className,
  variant = 'shadcn', // 'shadcn' uses <Input>, 'native' uses <input>
  'data-testid': dataTestid,
}, ref) => {
  const [value, setValue] = useState('');

  // Allow the parent to clear all instances together (e.g. "Clear All Boards")
  useEffect(() => {
    if (resetKey !== undefined) setValue('');
  }, [resetKey]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const v = value;
      if (v && v.trim()) {
        onSubmit && onSubmit(v);
        setValue('');
      }
    }
  };

  if (variant === 'native') {
    return (
      <input
        ref={ref}
        data-testid={dataTestid}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        className={className}
      />
    );
  }
  return (
    <Input
      ref={ref}
      data-testid={dataTestid}
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      className={className}
    />
  );
});

AutoMarkupInput.displayName = 'AutoMarkupInput';
export default memo(AutoMarkupInput);
